import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

const FILTERS = [
  { key: "pending_approval", labelKey: "admin_agencies.filter_pending" },
  { key: "active", labelKey: "admin_agencies.filter_active" },
  { key: "rejected", labelKey: "admin_agencies.filter_rejected" },
  { key: "all", labelKey: "admin_agencies.filter_all" },
];

function formatDate(iso) {
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  } catch {
    return iso;
  }
}

export default function AdminAgencies() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/agencies", { params });
      setItems(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          t("common.error_generic")
      );
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (orgId, action) => {
    setBusyId(orgId);
    try {
      await api.post(`/admin/agencies/${orgId}/${action}`);
      toast.success(
        action === "approve"
          ? t("admin_agencies.approved_toast")
          : t("admin_agencies.rejected_toast")
      );
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          t("common.error_generic")
      );
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-agencies-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            {t("admin_agencies.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("admin_agencies.subtitle")}</p>
        </div>

        <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-agencies-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={
                "rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
                (filter === f.key
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100")
              }
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">{t("admin_agencies.col_agency")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_siret")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_website")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_specialties")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_contact")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_submitted")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_status")}</th>
                  <th className="px-4 py-3">{t("admin_agencies.col_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                      {t("common.loading")}
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-slate-500" data-testid="admin-agencies-empty">
                      {t("admin_agencies.empty")}
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((it) => (
                    <tr key={it.organization.id} data-testid={`agency-row-${it.organization.id}`} className="align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{it.organization.name}</div>
                        {it.organization.application_notes && (
                          <div className="mt-1 max-w-xs text-xs text-slate-500">
                            <span className="font-semibold text-slate-600">
                              {t("admin_agencies.notes_label")}:
                            </span>{" "}
                            {it.organization.application_notes}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{it.organization.siret || "—"}</td>
                      <td className="px-4 py-3">
                        {it.organization.website ? (
                          <a
                            href={it.organization.website}
                            target="_blank"
                            rel="noreferrer"
                            className="text-indigo-700 hover:text-indigo-800"
                          >
                            {it.organization.website}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(it.organization.specialties || []).map((s) => (
                            <span key={s} className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                              {s}
                            </span>
                          ))}
                          {(!it.organization.specialties || it.organization.specialties.length === 0) && "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>{it.applicant_name}</div>
                        <div className="text-xs text-slate-500">{it.applicant_email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{formatDate(it.submitted_at)}</td>
                      <td className="px-4 py-3">
                        <StatusPill status={it.organization.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => act(it.organization.id, "approve")}
                            disabled={
                              busyId === it.organization.id ||
                              it.organization.status === "active"
                            }
                            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`approve-btn-${it.organization.id}`}
                          >
                            {t("admin_agencies.approve")}
                          </button>
                          <button
                            onClick={() => act(it.organization.id, "reject")}
                            disabled={
                              busyId === it.organization.id ||
                              it.organization.status === "rejected"
                            }
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`reject-btn-${it.organization.id}`}
                          >
                            {t("admin_agencies.reject")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatusPill({ status }) {
  const { t } = useTranslation();
  const map = {
    pending_approval: "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
    active: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
    rejected: "bg-rose-50 text-rose-700 ring-1 ring-rose-100",
    suspended: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider " +
        (map[status] || map.suspended)
      }
      data-testid={`status-pill-${status}`}
    >
      {t(`admin_agencies.status_${status}`)}
    </span>
  );
}
